<script setup lang="ts">
import { useRafFn } from '@vueuse/core';
import { Assert } from 'shared/assert';
import { calculateHighlightColor } from 'shared/color';
import { Circle, createCircle } from 'shared/common/domain/circle';
import { createPoint, Point } from 'shared/common/domain/point';
import { Message } from 'primevue';
import { computed, onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);

const canvasRef = useTemplateRef('coil-stack-canvas');
let canvas: Canvas | null = null;

useRafFn(() => render());

defineExpose({
  isMovable
});

const model = defineModel<CoilStackPositionSelectModel>();

const placeCoil = defineModel<CoilModel>('place-coil');
const selectedCoil = defineModel<CoilModel>('selected-coil');

const props = defineProps<{
  mode: CoilStackPositionSelectMode,
  sameStorageArea?: boolean
}>();

const mouse = {
  point: new Point(0,0),
  active: false
};

const viewModel: CoilStackPositionSelectViewModel = {
  coils: [],
  height: 0,
  width: 0,
  renderScheduled: true,
  pixelPerMillimeter: 0,
};

const coilMap = new Map<string, CoilViewModel>();
const undrawnCoils = ref<CoilModel[]>([]);

const warning = computed(() => {
  const coilNames = undrawnCoils.value.map(c => c.name);

  if (coilNames.length > 0) {
    return translations.value.coils.unableToDrawCoils(coilNames);
  }

  return undefined;
});

watch(selectedCoil, () => viewModel.renderScheduled = true);

watch([model, placeCoil], () => {

  const coilStack = model.value;

  viewModel.renderScheduled = true;

  viewModel.coils.length = 0;
  viewModel.height = 0;
  viewModel.width = 0;
  viewModel.pixelPerMillimeter = 0;

  if (!coilStack) {
    return;
  }

  const maxLevel = coilStack.maxLevel;
  const maxHeight = coilStack.maxHeight;
  const maxCoilDiameter = coilStack.maxCoilDiameter;

  const {
    slots,
    slotsWidth,
  } = coilStack;

  viewModel.pixelPerMillimeter = coilStack.pixelPerMillimeter;

  const marginY = 5 / viewModel.pixelPerMillimeter;
  const marginX = 5 / viewModel.pixelPerMillimeter;

  let height = 0;
  const width = slots * slotsWidth + marginX * 2 + (2 * slotsWidth);

  coilMap.clear();
  undrawnCoils.value.length = 0;

  const defaultColor = '#BDBDBD';

  for (const coil of coilStack.coils) {
    if (coil.level !== 0) continue;

    const coilKey = createCoilKey(coil);
    const current = coilMap.get(coilKey);

    if (current) {
      undrawnCoils.value.push(coil);
      continue;
    }

    const radius = coil.diameter / 2;
    const x = ((coil.position / 2) * slotsWidth) + marginX + slotsWidth;
    const y = radius + marginY;

    const circle = createCircle({
        x,
        y,
        radius
    });

    const coilModel: CoilViewModel = {
      circle: circle,
      color: defaultColor,
      model: coil,
      placeholder: false,
      onTop: true
    };

    const coilHeight = circle.y + circle.radius;

    if (coilHeight > height) {
      height = coilHeight;
    }

    viewModel.coils.push(coilModel);
    coilMap.set(coilKey, coilModel);
  }

  let currentLevel = 0;

  for (const coil of coilStack.coils.sort((a, b) => a.level - b.level)) {
    if (coil.level === 0) continue;

    const coilKey = createCoilKey(coil);
    const current = coilMap.get(coilKey);

    if (current) {
      undrawnCoils.value.push(coil);
      continue;
    }

    const leftNeighbor = findLeftNeighbor({
      level: coil.level - 1,
      position: coil.position,
      maxDistance: 2
    });

    const rightNeighbor = findRightNeighbor({
      level: coil.level - 1,
      position: coil.position,
      maxDistance: 2
    });

    if (!leftNeighbor || !rightNeighbor) {
      undrawnCoils.value.push(coil);
      continue;
    }

    const leftCoil = leftNeighbor.coil;
    const rightCoil = rightNeighbor.coil;

    const radius = coil.diameter / 2;

    const leftCircle = leftCoil.circle.addToRadius(radius);
    const rightCircle = rightCoil.circle.addToRadius(radius);

    const intersections = leftCircle.intersect(rightCircle);

    if (intersections.length === 0) {
      undrawnCoils.value.push(coil);
      continue;
    }

    let point: Point | null = null;

    for (const p of intersections) {
      if (p.y > leftCoil.circle.y && p.y > rightCoil.circle.y) {
        point = p;
        break;
      }
    }

    if (!point) {
      undrawnCoils.value.push(coil);
      continue;
    }

    const circle = new Circle(point, radius);

    const coilModel: CoilViewModel = {
      circle: circle,
      color: defaultColor,
      model: coil,
      placeholder: false,
      onTop: true
    };

    const coilHeight = circle.y + circle.radius;

    if (coilHeight > height) {
      height = coilHeight;
    }

    viewModel.coils.push(coilModel);

    coilMap.set(coilKey, coilModel);

    if (coil.level > currentLevel) {
      currentLevel = coil.level;
    }

    rightCoil.onTop = false;
    leftCoil.onTop = false;
  }

  generatePlaceholders();

  viewModel.height = height + marginY;
  viewModel.width = width;

  function findCoilById(id: string) {
    for (const coil of coilMap.values()) {
      if (coil.model.id === id) return coil;
    }
  }

  function generatePlaceholders() {
    if (props.mode !== 'place') return;
    const placeCoilValue = placeCoil.value;
    if (!placeCoilValue) return;
    if (maxCoilDiameter !== null && maxCoilDiameter < placeCoilValue.diameter) return;
    const placeCoilViewModel = findCoilById(placeCoilValue.id);
    if (placeCoilViewModel && !placeCoilViewModel.onTop) return;
    generateBottomPlaceholders(placeCoilValue);
    generateStackedPlaceholders(placeCoilValue);
  }

  function generateBottomPlaceholders(placeCoilValue: CoilModel) {

    const {
       diameter: placeCoilDiameter
    } = placeCoilValue;

    const placeCoilRadius = placeCoilDiameter / 2;

    const maxPosition = (slots - 1) * 2;

    for (let position = 0; position <= maxPosition; position += 2) {
      const key = createCoilKey({ level: 0, position: position});
      const coil = coilMap.get(key);
      if (coil) continue;

      if (props.sameStorageArea && placeCoilValue.position === position && placeCoilValue.level === 0) continue;

      const leftNeighbor = findLeftNeighbor({
        level: 0,
        position: position,
        maxDistance: 2,
        avoidPlaceCoil: true
      });

      if (leftNeighbor && (leftNeighbor.coil.circle.radius + placeCoilRadius) > slotsWidth * leftNeighbor.distance / 2) continue;

      const rightNeighbor = findRightNeighbor({
        level: 0,
        position: position,
        maxDistance: 2,
        avoidPlaceCoil: true
      });

      if (rightNeighbor && (rightNeighbor.coil.circle.radius + placeCoilRadius) > slotsWidth * rightNeighbor.distance / 2) continue;

      const radius = placeCoilRadius;

      const x = ((position / 2) * slotsWidth) + marginX + slotsWidth;
      const y = placeCoilRadius + marginY;

      const circle = createCircle({
          x,
          y,
          radius
      });

      if (intersecting(circle)) continue;

      const coilModel: CoilViewModel = {
        circle: circle,
        color: defaultColor,
        model: {
          id: crypto.randomUUID(),
          name: '',
          diameter: placeCoilDiameter,
          level: 0,
          position: position
        },
        placeholder: true,
        onTop: true
      };

      const coilHeight = circle.y + circle.radius;

      if (coilHeight > height) {
        height = coilHeight;
      }

      viewModel.coils.push(coilModel);
      const coilKey = createCoilKey(coilModel.model);
      coilMap.set(coilKey, coilModel);
    }
  }

  function generateStackedPlaceholders(placeCoilValue: CoilModel) {

    const {
       diameter: placeCoilDiameter
    } = placeCoilValue;

    const placeCoilRadius = placeCoilDiameter / 2;

    let done = false;
    let level = 1;
    const maxPosition = (slots - 1) * 2;

    while (!done) {
      done = level > currentLevel;

      if (maxLevel !== null && level >= maxLevel) break;

      for (let position = 0; position < maxPosition; position += 1) {

        const key = createCoilKey({ level: level, position: position});
        const currentCoil = coilMap.get(key);

        if (currentCoil) continue;


        if (props.sameStorageArea && placeCoilValue.position === position && placeCoilValue.level === level) continue;

        const leftNeighbor = findLeftNeighbor({
          level: level - 1,
          position: position,
          maxDistance: 2,
          avoidPlaceCoil: true
        });

        const rightNeighbor = findRightNeighbor({
          level: level - 1,
          position: position,
          maxDistance: 2,
          avoidPlaceCoil: true
        });

        if (!leftNeighbor || !rightNeighbor) continue;

        const leftCoil = leftNeighbor.coil;
        const rightCoil = rightNeighbor.coil;

        const radius = placeCoilRadius;

        const leftCircle = leftCoil.circle.addToRadius(radius);
        const rightCircle = rightCoil.circle.addToRadius(radius);

        const intersections = leftCircle.intersect(rightCircle);

        if (intersections.length === 0) continue;

        let point: Point | null = null;

        for (const p of intersections) {
          if (p.y > leftCoil.circle.y && p.y > rightCoil.circle.y) {
            point = p;
            break;
          }
        }

        if (!point) continue;

        const circle = new Circle(point, radius);
        const coilHeight = circle.y + circle.radius;

        if (maxHeight && coilHeight > maxHeight) {
          continue;
        }

        if (intersecting(circle)) continue;

        const coil: CoilModel = {
          diameter: placeCoilValue.diameter,
          level: level,
          name: '',
          position: position,
          id: crypto.randomUUID(),
        };

        const coilModel: CoilViewModel = {
          circle: circle,
          color: defaultColor,
          model: coil,
          placeholder: true,
          onTop: true
        };


        if (coilHeight > height) {
          height = coilHeight;
        }

        viewModel.coils.push(coilModel);
        const coilKey = createCoilKey(coil);
        coilMap.set(coilKey, coilModel);
        done = false;
      }

      level += 1;
    }
  }

  function intersecting(circle: Circle) {
    for (const other of coilMap.values()) {
      if (other.placeholder) continue;
      if (other.circle.isIntersecting(circle)) {
        return true;
      }
    }

    return false;
  }

  function findLeftNeighbor(query: FindCoilNeighbor) {
    const startPosition = query.position - 1;
    const maxPosition = query.position - query.maxDistance;
    let distance = 1;

    const avoidPlaceCoil = query.avoidPlaceCoil ?? false;

    for (let position = startPosition; position >= maxPosition; position--) {
      const neighborKey = createCoilKey({ level: query.level, position });
      const neighbor = coilMap.get(neighborKey);

      if (neighbor) {
        if (avoidPlaceCoil && neighbor.model.id === placeCoil.value?.id) continue;
        if (!neighbor.placeholder) return {
          coil: neighbor,
          distance
        };
      }

      distance += 1;
    }

    return null;
  }

  function findRightNeighbor(query: FindCoilNeighbor) {
    const startPosition = query.position + 1;
    const maxPosition = query.position + query.maxDistance;
    let distance = 1;

    const avoidPlaceCoil = query.avoidPlaceCoil ?? false;

    for (let position = startPosition; position <= maxPosition; position++) {
      const neighborKey = createCoilKey({ level: query.level, position });
      const neighbor = coilMap.get(neighborKey);
      if (neighbor) {
        if (avoidPlaceCoil && neighbor.model.id === placeCoil.value?.id) continue;
        if (!neighbor.placeholder) return {
          coil: neighbor,
          distance
        };
      }
      distance += 1;
    }

    return null;
  }

}, {
  immediate: true
});

onMounted(() => {
  const { element } = getCanvas();
  element.addEventListener('mousemove', onMouseMove);
  element.addEventListener('click', onClick);
  element.addEventListener('mouseleave', onMouseLeave);
  element.addEventListener('mouseenter', onMouseMove);
});

onUnmounted(() => {
  if (!canvas) return;
  const { element } = canvas;
  element.removeEventListener('mousemove', onMouseMove);
  element.removeEventListener('click', onClick);
  element.removeEventListener('mouseleave', onMouseLeave);
  element.removeEventListener('mouseenter', onMouseMove);
});

function createCoilKey(coil: { level: number, position: number }) {
  return `l${coil.level}p${coil.position}`;
}

function onMouseMove(ev: MouseEvent) {
  mouseEventToWorld(ev);
  mouse.active = true;
  viewModel.renderScheduled = true;
}

function onMouseLeave(ev: MouseEvent) {
  mouseEventToWorld(ev);
  mouse.active = false;
  viewModel.renderScheduled = true;
}

function mouseEventToWorld(ev: MouseEvent) {
  const worldPoint = pixelToWorld(createPoint({ x: ev.offsetX, y: ev.offsetY }));
  mouse.point = worldPoint;
}

function pixelToWorld(p1: Point) {
  const {
    pixelPerMillimeter,
    height
  } = viewModel;

  const result = createPoint({
    x: p1.x / pixelPerMillimeter,
    y: (p1.y / -pixelPerMillimeter) + height
  });

  return result;
}

function onClick(ev: MouseEvent) {
  mouseEventToWorld(ev);
  for (const coil of viewModel.coils) {
    const circle = coil.circle;
    const distance = mouse.point.distance(circle.center);
    if (distance < circle.radius && selectable(coil)) {
      selectedCoil.value = coil.model;
      viewModel.renderScheduled = true;
      return;
    }
  }

  selectedCoil.value = undefined;
  viewModel.renderScheduled = true;
}

function isMovable(model: CoilModel) {
  const viewModel = coilMap.get(createCoilKey(model));
  if (!viewModel) return true;
  return !viewModel.placeholder && viewModel.onTop;
}

function selectable(coil: CoilViewModel) {
  if (props.mode === 'place') {
    return coil.placeholder;
  } else {
    return !coil.placeholder && coil.onTop;
  }
}

function getCanvas() : Canvas {
  if (canvas) return canvas;

  const element = canvasRef.value;
  Assert.definied(element);

  const context = element.getContext('2d');
  Assert.definied(context);
  canvas = {
    context,
    element
  };
  return canvas;
}

function render() {
  const {
    renderScheduled,
    coils,
    width,
    height,
    pixelPerMillimeter
  } = viewModel;

  if (!renderScheduled) return;
  viewModel.renderScheduled = false;

  const {
    element,
    context
  } = getCanvas();

  const pixelHeight = Math.ceil(height * pixelPerMillimeter);

  element.height = pixelHeight;
  element.style.height = `${pixelHeight}px`;

  const pixelWidth = Math.ceil(width * pixelPerMillimeter);

  element.width = pixelWidth;
  element.style.width = `${pixelWidth}px`;

  const canvasWidth = element.width;
  const canvasHeight = element.height;

  context.clearRect(0, 0, canvasWidth, canvasHeight);

  context.save();
  context.scale(pixelPerMillimeter, -pixelPerMillimeter);
  context.translate(0, -height);

  const endAngle = 2 * Math.PI;

  for (const coil of coils) {

    let color = coil.color;
    let lineColor: string|null = null;
    const circle = coil.circle;

    if (coil.placeholder) {
      lineColor = '#BDBDBD';
    }

    let hover = false;

    if (mouse.active && selectable(coil)) {
      const distanceToMouse = mouse.point.distance(circle.center);
      hover = distanceToMouse < circle.radius;
    }

    const selected = selectedCoil.value?.id === coil.model.id ||
      placeCoil.value?.id === coil.model.id;

    if (selected) {
      color = calculateHighlightColor(color);
      lineColor = '#1E88E5';
    } else if (hover) {
      color = calculateHighlightColor(color);
    }

    if (!coil.placeholder) {
      context.beginPath();
      context.fillStyle = color;
      context.arc(circle.x, circle.y, circle.radius, 0, endAngle, false);
      context.arc(circle.x, circle.y, 150, 0, endAngle, true);
      context.fill();
    }

    context.beginPath();

    context.arc(circle.x, circle.y, circle.radius, 0, endAngle, false);

    if (lineColor) {
      context.strokeStyle = lineColor;
      context.lineWidth = 2/pixelPerMillimeter;
      if (coil.placeholder) {
        context.setLineDash([4/pixelPerMillimeter, 2/pixelPerMillimeter]);
      }
      context.stroke();
    }
  }

  context.restore();
}

interface FindCoilNeighbor {
  level: number;
  position: number;
  maxDistance: 1 | 2;
  avoidPlaceCoil?: boolean;
}

export interface PlaceCoilModel {
  id: number;
  diameter: number;
}

export type CoilStackPositionSelectMode = 'take' | 'place';

export interface CoilStackPositionSelectModel {
  id: string;
  name: string;
  slots: number;
  slotsWidth: number;
  maxLevel: number | null;
  maxHeight: number | null;
  maxCoilDiameter: number | null;
  coils: CoilModel[];
  pixelPerMillimeter: number;
}

export interface CoilModel {
  id: string;
  name: string;
  position: number;
  level: number;
  diameter: number;
}

export interface CoilStackPositionSelectViewModel {
  coils: CoilViewModel[];
  height: number;
  width: number;
  renderScheduled: boolean;
  pixelPerMillimeter: number;
}

export interface CoilViewModel {
  circle: Circle;
  color: string;
  model: CoilModel;
  placeholder: boolean;
  onTop: boolean;
}

interface Canvas {
  element: HTMLCanvasElement;
  context: CanvasRenderingContext2D;
}

</script>

<template>
  <div class="coil-stack-grid">
    <canvas ref="coil-stack-canvas" width="500" height="200">
    </canvas>
    <Message v-if="warning" severity="warn">{{ warning }}</Message>
  </div>
</template>

<style>

.coil-stack-grid {
  display: grid;
  overflow-x: auto;
  padding: .5rem;
}

</style>
